import { useState, useEffect, useContext } from 'react';
import { Box, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LanguageIcon from '@mui/icons-material/Language';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
    AppContext,
    SttButtonFab,
    SttChipEventKind,
    SttChipSport,
    StatusDot,
    SttTable,
    SttTopControls,
    SttLabelPeriod,
    SttPage,
} from '../../../sporttia/all';
import { useToggle, useTuples, useCrud } from '../../../lib/hooks';
import { sporttiaColors } from '../../../theme/theme';
import Event from './Event';
import { PERMISSIONS } from '../../../lib/permissions';

type Sport = {
    id: number;
    name?: string;
};

type EventKind = 'RACE' | 'TOURNAMENT' | 'HANGOUT';

type EventRow = {
    id: string | number;
    name: string;
    kind?: EventKind;
    status?: string;
    sport?: Sport;
    numPeriods?: number;
    numTeams?: number;
    inscIni?: string;
    inscEnd?: string;
    numParticipants?: number;
    ini?: string;
    end?: string;
    total?: number;
    debt?: number;
    trash?: boolean | number;
    onlyCertified?: boolean;
    showParticipants?: boolean;
    signupOnline?: boolean;
    paymentForms?: string[];
};

type EventsData = {
    count: number;
    rows: EventRow[];
};

type EventState = {
    id: string | number | null;
    trash: boolean | number;
};

type Filters = {
    name: string;
    year: number;
    status: string;
};

export default function Events(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { id: urlId, tab: urlTab } = useParams<{
        id: string;
        tab?: string;
    }>();
    const navigate = useNavigate();

    const [events, setEvents, , Get] = useCrud<EventsData>({
        count: 0,
        rows: [],
    });
    const [event, setEvent] = useState<EventState>({ id: null, trash: false });
    const [filters, setFilter] = useTuples<Filters>({
        name: '',
        year: new Date().getFullYear(),
        status: 'ACTIVE',
    });
    const [trash, toggleTrash] = useToggle(() => {
        loadEvents();
    });

    // Handle event ID from URL path parameter (e.g., /events/123)
    useEffect(() => {
        if (urlId) {
            setEvent({ id: Number(urlId), trash: false });
        }
    }, [urlId]);

    useEffect(() => {
        cxt?.setHeader({ title: t('Events') });
    }, []);

    function loadEvents(params: { rows?: number } = { rows: 20 }): void {
        if (filters?.status === 'ALL') {
            filters.status = '';
        }
        Get<EventsData>(`/scs/${cxt?.sc?.id}/events`, {
            ...params,
            ...filters,
            trash,
        }).then((data: any) => setEvents(data));
        if (filters?.status === '') {
            filters.status = 'ALL';
        }
    }

    function goCreateNew(): void {
        setEvent({
            id: 'create',
            trash: 0,
        });
    }

    return (
        <SttPage title={t('Events')}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        type: 'text',
                        name: 'name',
                        value: filters.name,
                        onEnterKey: loadEvents,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        caption: t('Year'),
                        type: 'year',
                        name: 'year',
                        value: filters.year,
                        years: {
                            start: new Date().getFullYear(),
                            past: 5,
                            future: 5,
                        },
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 6,
                    },
                    {
                        caption: t('Status'),
                        type: 'select',
                        name: 'status',
                        value:
                            filters.status === 'ALL'
                                ? '0'
                                : filters.status === 'ACTIVE'
                                  ? '1'
                                  : '2',
                        mandatory: false,
                        cleanable: false,
                        options: [
                            {
                                label: t('All'),
                                value: '0',
                            },
                            {
                                label: t('Active'),
                                value: '1',
                            },
                            {
                                label: t('Inactive'),
                                value: '2',
                            },
                        ],
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 6,
                    },
                ]}
                onToggleTrash={toggleTrash}
                onChange={({ name, value }) => {
                    if (name === 'status') {
                        setFilter({
                            name: name as keyof Filters,
                            value:
                                value === '0'
                                    ? 'ALL'
                                    : value === '1'
                                      ? 'ACTIVE'
                                      : 'NOT_ACTIVE',
                        });
                    } else if (
                        name === 'name' ||
                        name === 'year' ||
                        name === 'status'
                    ) {
                        setFilter({
                            name: name as keyof Filters,
                            value: value as string | number,
                        });
                    }
                }}
                onFilter={loadEvents}
            />
            <SttTable
                onFetch={loadEvents}
                data={events}
                columns={[
                    {
                        title: '',
                        width: 40,
                        value: (row: EventRow) => (
                            <StatusDot active={row.status === 'ACTIVE'} />
                        ),
                    },
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        align: 'left',
                        onClick: (row: EventRow) =>
                            setEvent({
                                id: row.id,
                                trash: row.trash || false,
                            }),
                    },
                    {
                        title: t('Type'),
                        value: (row: EventRow) =>
                            row.kind ? (
                                <SttChipEventKind kind={row.kind} />
                            ) : null,
                    },
                    {
                        title: t('Sport'),
                        value: (row: EventRow) =>
                            row.sport ? (
                                <SttChipSport sport={row.sport} />
                            ) : null,
                    },
                    {
                        title: t('Status'),
                        value: (row: EventRow) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Tooltip
                                    title={t('sc.event.main.certifiedUsers')}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: row.onlyCertified
                                                ? alpha(
                                                      sporttiaColors.primary
                                                          .main,
                                                      0.15,
                                                  )
                                                : sporttiaColors.icon
                                                      .disabledBg,
                                        }}
                                    >
                                        <VerifiedUserIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.onlyCertified
                                                    ? 'var(--brand-text)'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip
                                    title={t('sc.event.main.showLastUsers')}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor:
                                                row.showParticipants
                                                    ? alpha(
                                                          sporttiaColors.info
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                        }}
                                    >
                                        <VisibilityIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.showParticipants
                                                    ? 'info.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip
                                    title={t('page.sc.events.signupOnline')}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: row.signupOnline
                                                ? alpha(
                                                      sporttiaColors.success
                                                          .main,
                                                      0.15,
                                                  )
                                                : sporttiaColors.icon
                                                      .disabledBg,
                                        }}
                                    >
                                        <LanguageIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.signupOnline
                                                    ? 'success.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip title={t('page.sc.events.later')}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor:
                                                row.paymentForms?.includes('')
                                                    ? alpha(
                                                          sporttiaColors.warning
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                        }}
                                    >
                                        <ScheduleIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.paymentForms?.includes(
                                                    '',
                                                )
                                                    ? 'warning.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip title={t('page.sc.events.purse')}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor:
                                                row.paymentForms?.includes(
                                                    'PURSE',
                                                )
                                                    ? alpha(
                                                          sporttiaColors
                                                              .secondary.main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                        }}
                                    >
                                        <AccountBalanceWalletIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.paymentForms?.includes(
                                                    'PURSE',
                                                )
                                                    ? 'secondary.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Tooltip title={t('page.sc.events.tpv')}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor:
                                                row.paymentForms?.includes(
                                                    'TPV',
                                                )
                                                    ? alpha(
                                                          sporttiaColors.error
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                        }}
                                    >
                                        <CreditCardIcon
                                            sx={{
                                                fontSize: 18,
                                                color: row.paymentForms?.includes(
                                                    'TPV',
                                                )
                                                    ? 'error.main'
                                                    : sporttiaColors.icon
                                                          .disabled,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                            </Box>
                        ),
                    },
                    {
                        title: t('Timetables'),
                        type: 'nonZero',
                        field: 'numPeriods',
                    },
                    {
                        title: `${t('Teams')} / ${t('Users')}`,
                        align: 'center',
                        value: (row: EventRow) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <span>{row.numTeams || 0}</span>
                                <span>/</span>
                                <span>{row.numParticipants || 0}</span>
                            </Box>
                        ),
                    },
                    {
                        title: `${t('Inscription')} / ${t('Period')}`,
                        width: 180,
                        value: (row: EventRow) => (
                            <Box>
                                <SttLabelPeriod
                                    ini={row.inscIni || ''}
                                    end={row.inscEnd || ''}
                                />
                                <Box>
                                    <SttLabelPeriod
                                        ini={row.ini || ''}
                                        end={row.end || ''}
                                    />
                                </Box>
                            </Box>
                        ),
                    },
                    {
                        title: t('Total'),
                        type: 'debt',
                        field: 'total',
                        align: 'right',
                    },
                    {
                        title: t('Debt'),
                        type: 'debt',
                        field: 'debt',
                        align: 'right',
                    },
                ]}
            />
            <SttButtonFab
                onClick={goCreateNew}
                permission={PERMISSIONS.EVENT.CREATE}
            />

            {event.id && (
                <Event
                    id={event.id}
                    trash={event.trash}
                    initialTabKey={urlTab}
                    onChange={(newEvent) => {
                        // Map the Event type to EventRow type
                        const updatedRow: EventRow = {
                            id: newEvent.id || 0,
                            name: newEvent.name,
                            kind: newEvent.kind as EventKind | undefined,
                            status: newEvent.status,
                            sport: newEvent.sport
                                ? {
                                      id:
                                          typeof newEvent.sport.id === 'number'
                                              ? newEvent.sport.id
                                              : parseInt(
                                                    String(newEvent.sport.id),
                                                ),
                                      name: newEvent.sport.name,
                                  }
                                : undefined,
                            inscIni: newEvent.inscIni,
                            inscEnd: newEvent.inscEnd,
                            ini: newEvent.ini,
                            end: newEvent.end,
                            trash: newEvent.trash,
                        };
                        // Safe update with fallback for when events list hasn't loaded yet
                        const currentRows = events?.rows || [];
                        setEvents({
                            ...events,
                            count: events?.count || 0,
                            rows: currentRows
                                .map((e) =>
                                    e.id === updatedRow.id ? updatedRow : e,
                                )
                                .filter((e) => !!trash || (!trash && !e.trash)),
                        });
                    }}
                    onClose={() => {
                        setEvent({ id: null, trash: false });
                        // Navigate back to events list if we came from a direct URL
                        if (urlId) {
                            navigate('/events');
                        }
                        loadEvents();
                    }}
                />
            )}
        </SttPage>
    );
}
